import { Helmet } from 'react-helmet-async'
import { useState, useMemo, useCallback, useRef } from 'react'
import FilterDropdown from 'src/components/atoms/FilterDropDown'
import { MapSearchBar } from 'src/components/molecules/MapSearchBar'
import MapLoadingHolder from 'src/components/atoms/map-loading-holder'
import ReworkMapBox from 'src/components/organisms/mapbox/ReworkMapBox'
import DashboardHeader from 'src/components/molecules/DashboardHeader'
import { ProjectsQueryParams } from '../../types/projects'
import { api } from '../../api/root'
import { ProjectType } from '../../types/type'
import { useChatStore } from '../../enviroment/store/chatStore'
import { useShallow } from 'zustand/react/shallow'
import { createGeoJSONFileFromFeatures } from '../../enviroment/utils/createGeoJSONFileFromFeatures'
import useAuth from '../../enviroment/hooks/useAuth'
import { UserRole } from '../../types/user'
import { PrimaryButton } from '../../components/atoms/buttons/PrimaryButton'
import { IconWorldUpload } from '@tabler/icons-react'
import AreaSizeMessage from '../../components/molecules/AreaSizeMessage'

const AtlasPage: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const handleMapLoading = () => setLoading(false)

  const fetchConfig: ProjectsQueryParams = {
    includePublic: true,
    includeTenant: true,
  }

  const [selectedTypesIds, setSelectedTypesIds] = useState<string[]>([])
  const [selectedStagesIds, setSelectedStagesIds] = useState<string[]>([])
  const [selectedLocations, setSelectedLocations] = useState<string[]>([])
  const { data: projects, refetch } = api.project.atlas.useQuery({
    ...fetchConfig,
    typeIds: selectedTypesIds,
    stageIds: selectedStagesIds,
    location: selectedLocations,
  })

  const { data: typesData, fetchNextPage, hasNextPage } = api.type.projectTypes.useInfiniteQuery()
  const types = useMemo(() => typesData?.pages.flat() ?? [], [typesData])

  const { data: stages } = api.stage.projectStages.useQuery()
  const { data: locations } = api.location.projectLocations.useQuery(fetchConfig)

  const user = useAuth()

  const formattedLocations = useMemo(
    () =>
      locations?.map((location) => ({
        id: location,
        name: location,
      })) as unknown as ProjectType[],
    [locations],
  )

  const flyToRef = useRef<((center: [number, number], bbox?: [number, number, number, number]) => void) | null>(null)

  const handleSearchSelect = useCallback((center: [number, number], bbox?: [number, number, number, number]) => {
    flyToRef.current?.(center, bbox)
  }, [])

  const { mutateAsync: validateZone, isLoading: isValidating } = api.project.validate.useMutation()

  const { upsertMessageByType, setIsExpanded, setCreateProjectFormData } = useChatStore(
    useShallow((s) => ({
      upsertMessageByType: s.upsertMessageByType,
      setIsExpanded: s.setIsExpanded,
      setCreateProjectFormData: s.setCreateProjectFormData,
    })),
  )

  const [drawnHectares, setDrawnHectares] = useState<number | null>(null)
  const [limitExceeded, setLimitExceeded] = useState(false)

  const handleAreaCreated = useCallback(
    async (features: GeoJSON.Feature[], totalHectares: number) => {
      if (!features || features.length === 0) {
        setCreateProjectFormData({ drawnFeatures: null })
        setDrawnHectares(null)
        setLimitExceeded(false)
        return
      }

      setCreateProjectFormData({ drawnFeatures: features })
      setDrawnHectares(totalHectares)
      setLimitExceeded(false)
      const file = createGeoJSONFileFromFeatures(features)

      if (file) {
        try {
          const result = await validateZone(file)

          setLimitExceeded(result.limitExceeded)
          upsertMessageByType(result.limitExceeded ? 'area-limit' : 'create-project')
          setIsExpanded(true)
        } catch {
          setLimitExceeded(false)
        }
      }
    },
    [validateZone, setCreateProjectFormData],
  )

  const handleAreaDeleted = useCallback(() => {
    setCreateProjectFormData({ drawnFeatures: null })
    setDrawnHectares(null)
    setLimitExceeded(false)
  }, [setCreateProjectFormData])

  const handleUploadFileClick = useCallback(() => {
    upsertMessageByType('create-project')
    setIsExpanded(true)
  }, [upsertMessageByType, setIsExpanded])

  const handleCreateProjectClick = useCallback(() => {
    upsertMessageByType('create-project')
    setIsExpanded(true)
  }, [upsertMessageByType, setIsExpanded])

  return (
    <>
      <Helmet>
        <title>RAMO - Atlas</title>
      </Helmet>

      <div className="relative h-full w-full">
        {/* Map as full background */}
        <div className="absolute inset-0">
          <ReworkMapBox
            projects={projects}
            initialOptions={{
              style: 'mapbox://styles/mapbox/standard-satellite',
              projection: 'globe',
            }}
            onLoaded={handleMapLoading}
            refetch={refetch}
            onAreaCreated={user.userRole === UserRole.VIEWER ? undefined : handleAreaCreated}
            onAreaDeleted={handleAreaDeleted}
            onUploadFileClick={user.userRole === UserRole.VIEWER ? undefined : handleUploadFileClick}
            onSearchFlyTo={(flyTo) => {
              flyToRef.current = flyTo
            }}
          />
        </div>

        {/* Header overlaying the map */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-20">
          <div className="pointer-events-auto">
            <DashboardHeader
              title="Welcome to Atlas"
              subtitle="Select a project to start ..."
              isGradient={true}
              rightSide={
                user.userRole === UserRole.VIEWER ? undefined : (
                  <PrimaryButton
                    size="sm"
                    className="min-w-[160px]"
                    onClick={handleCreateProjectClick}
                    rightIcon={<IconWorldUpload size={20} />}
                  >
                    Create project
                  </PrimaryButton>
                )
              }
            />
          </div>
        </div>

        {/* Filters + Search - positioned below header */}
        <div className="absolute left-5 top-24 z-10 flex gap-2 p-2">
          <MapSearchBar onSelect={handleSearchSelect} className="w-full min-w-[260px] sm:w-[260px]" />
          {types && (
            <FilterDropdown
              type="Types"
              theme="dark"
              options={types}
              selectedIds={selectedTypesIds}
              onChange={(ids) => setSelectedTypesIds(ids)}
              className="w-full min-w-[260px] sm:w-[260px]"
              onScrollEnd={() => hasNextPage && fetchNextPage()}
            />
          )}
          {stages && (
            <FilterDropdown
              type="Stages"
              theme="dark"
              options={stages}
              selectedIds={selectedStagesIds}
              onChange={(ids) => setSelectedStagesIds(ids)}
              className="w-full min-w-[150px] sm:w-[150px]"
            />
          )}
          {formattedLocations && (
            <FilterDropdown
              type="Location"
              theme="dark"
              options={formattedLocations}
              selectedIds={selectedLocations}
              onChange={(ids) => setSelectedLocations(ids)}
              className="w-full min-w-[150px] sm:w-[150px]"
            />
          )}
        </div>

        {drawnHectares !== null && (
          <div className="absolute bottom-[72px] left-7 z-10">
            <AreaSizeMessage hectares={drawnHectares} isValidating={isValidating} limitExceeded={limitExceeded} />
          </div>
        )}

        {loading && <MapLoadingHolder />}
      </div>
    </>
  )
}

export default AtlasPage
